import { supabase } from '../lib/supabase';

import { getApplicationStatistics } from './applicationTrackingService';

const ensureUserId = async (): Promise<string> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (user?.id) return user.id;
  throw new Error('Not authenticated');
};

export interface JobReadinessResult {
  score: number;
  totalOptimizations: number;
  totalApplications: number;
  avgMatchScore: number | null;
  interviewRate: number;
  offerRate: number;
  recommendations: string[];
}

/**
 * Compute job readiness score (0–100) and recommendations from real data only.
 * Factors: optimizations count, average match score, applications sent, interview/offer rates.
 */
export async function getJobReadiness(): Promise<JobReadinessResult> {
  const userId = await ensureUserId();
  const recommendations: string[] = [];

  const [
    { data: optimizations },
    appStats
  ] = await Promise.all([
    supabase
      .from('llm_optimization_level2')
      .select('match_score')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(100),
    getApplicationStatistics().catch(() => ({
      totalApplications: 0,
      applicationsByStatus: {} as Record<string, number>,
      responseRate: 0,
      interviewRate: 0,
      offerRate: 0,
      averageResponseTime: 0,
      monthlyTrends: [],
      applicationsByChannel: {}
    }))
  ]);

  const totalOptimizations = optimizations?.length ?? 0;
  const totalApplications = appStats.totalApplications;
  let avgMatchScore: number | null = null;
  if (optimizations?.length) {
    const sum = optimizations.reduce((s, o) => s + (o.match_score ?? 0), 0);
    avgMatchScore = Math.round((sum / optimizations.length) * 10) / 10;
  }
  const interviewRate = appStats.interviewRate ?? 0;
  const offerRate = appStats.offerRate ?? 0;

  let score = 0;
  if (totalOptimizations >= 5) score += 25;
  else if (totalOptimizations >= 2) score += 15;
  else if (totalOptimizations >= 1) score += 5;
  if (avgMatchScore != null) {
    if (avgMatchScore >= 80) score += 25;
    else if (avgMatchScore >= 60) score += 18;
    else if (avgMatchScore >= 40) score += 10;
  }
  if (totalApplications >= 20) score += 25;
  else if (totalApplications >= 10) score += 18;
  else if (totalApplications >= 5) score += 12;
  else if (totalApplications >= 1) score += 5;
  if (offerRate > 0) score += 15;
  else if (interviewRate >= 20) score += 12;
  else if (interviewRate >= 10) score += 8;
  else if (interviewRate > 0) score += 5;
  score = Math.min(100, score);

  if (totalOptimizations === 0) recommendations.push('Optimize at least one resume for a target job to improve fit.');
  else if (totalOptimizations < 3) recommendations.push('Optimize 2–3 more resumes for different roles to widen options.');
  if (avgMatchScore != null && avgMatchScore < 60) recommendations.push('Focus on matching keywords and structure to job descriptions.');
  if (totalApplications === 0) recommendations.push('Start applying: track applications in Calendar & Notes.');
  else if (totalApplications < 5) recommendations.push('Apply to more quality roles; consistency improves odds.');
  if (interviewRate === 0 && totalApplications >= 3) recommendations.push('Review application materials and consider tailoring each application.');
  if (score >= 70 && recommendations.length === 0) recommendations.push('Keep applying and preparing for interviews.');

  return {
    score,
    totalOptimizations,
    totalApplications,
    avgMatchScore,
    interviewRate,
    offerRate,
    recommendations: recommendations.slice(0, 4)
  };
}

export interface ApplicationSentimentResult {
  total: number;
  byStatus: { status: string; label: string; count: number }[];
  responseRate: number;
  interviewRate: number;
  offerRate: number;
}

const STATUS_LABELS: Record<string, string> = {
  applied: 'Applied',
  viewed: 'Viewed',
  under_review: 'Under review',
  interview_scheduled: 'Interview',
  interview_completed: 'Interview done',
  offered: 'Offered',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn'
};

export async function getApplicationSentiment(): Promise<ApplicationSentimentResult> {
  const stats = await getApplicationStatistics().catch(() => ({
    totalApplications: 0,
    applicationsByStatus: {} as Record<string, number>,
    responseRate: 0,
    interviewRate: 0,
    offerRate: 0
  }));
  const byStatus = Object.entries(stats.applicationsByStatus || {}).map(([status, count]) => ({
    status,
    label: STATUS_LABELS[status] || status,
    count
  })).sort((a, b) => b.count - a.count);
  return {
    total: stats.totalApplications,
    byStatus,
    responseRate: stats.responseRate ?? 0,
    interviewRate: stats.interviewRate ?? 0,
    offerRate: stats.offerRate ?? 0
  };
}
